/** The user record editor's binding of the view-agnostic record core
 *  (`features/records/editor`) — everything FieldSlot and the Ledger need to know about a
 *  person that the catalog alone does not say (plan "Edit mode", D1, D6).
 *
 *  - Verdicts: a person has no feed of their own — a reservation system writes them
 *    through a booking it imports. Each field's verdict is edit_context
 *    `restech.verdicts`: the most severe over the user's open bookings, as the server
 *    states it (a changed field reads the verdict the PREVIEW plans for its change; an
 *    apply result carries none). The feeds that write the person name the "managed by"
 *    and the ledger banner says how many open bookings they come through.
 *  - Editability: the ConsoleUserEditEnabled kill switch, a soft-deleted user (read-only
 *    until restored), console_user_manage, each field's own permissions
 *    (console_user_view for PII and the contacts, console_perm_grant for Django admin
 *    access), the guards edit_context `blocked` reports (`not_self`, `not_guest`) and the
 *    tenant a tag needs.
 *  - Events: a user change emits no event unless Emit is chosen (`update_events`,
 *    default suppress — the ledger's switch).
 *  - Pickers: the account tags among the user's tenant's tags, with tenant + pk.
 *  - Preview and apply: `useUserChangesetPreview` / `useApplyUserChangeset`.
 */
import { useMemo } from 'react';

import { EVENT_STATUS_LABELS } from '@/api/bookingEdit';
import { tagOptions } from '@/api/bookings';
import {
  useApplyUserChangeset,
  useUserChangesetPreview,
  type UserRecordCatalog,
  type UserRecordCatalogField,
  type UserRecordCatalogOption,
  type UserRecordEditContext,
  type UserRecordRestechBooking,
} from '@/api/userRecord';
import type { ConsoleUserDetail } from '@/api/users';
import { recordKey, type DraftState, type RecordKey } from '@/features/records/draft/changeSet';
import type {
  RecordEditorConfig,
  RecordField,
  RecordOption,
  RecordOptionRef,
  RecordVerdict,
  RecordWidget,
} from '@/features/records/editor/types';
import {
  asContactsValue,
  contactsPreviewBody,
  userAddressAnswers,
  userAddressAsked,
  type ContactsValue,
  type UserAddressType,
} from '@/features/records/fields/widgets/contactsModel';
import { createTagsWidget } from '@/features/records/fields/widgets/pickers';
import { EDITING_OFF } from '@/features/records/header/editingOff';

import { userDisplayName } from '../userName';

/** Times on a person record (a receipt, "changed on the server at …"): a person spans
 *  tours, so UTC, labelled — the order record's rule. */
export const USER_TIMEZONE = 'UTC';
export const USER_MANAGE_PERM = 'console_user_manage';

/** A catalog field in the records core's vocabulary. */
function recordField(f: UserRecordCatalogField): RecordField {
  return { ...f, follows_lead: false };
}

/** A contacts question (`type: contact`) is a choice whose answers are rows of the
 *  draft's contacts value — the core asks it as a choice (`resolveOption` fills them). Its
 *  help is the catalog's, in the operator's words. */
function recordOption(o: UserRecordCatalogOption): RecordOption {
  return { ...o, type: 'choice' };
}

/** The contacts questions, by the contact type they decide. */
const ADDRESS_OPTIONS: Record<string, UserAddressType> = { user_email: 'email', user_phone: 'phone' };

/** The open bookings whose feed writes the person: a feed on the booking that writes the
 *  user's profile, or that a field's verdict names. */
export function feedingBookings(ctx: UserRecordEditContext): UserRecordRestechBooking[] {
  const named = new Set<number>();
  for (const v of Object.values(ctx.restech.verdicts)) {
    for (const id of [...v.bookings.overwrites, ...v.bookings.may_overwrite, ...v.bookings.suspended]) named.add(id);
  }
  return ctx.restech.bookings.filter((b) => b.managed_by && (b.writes_user || named.has(b.booking_id)));
}

/** Who feeds the person: one system by name ("Ventrata"), several as "A booking
 *  system"; null when no open booking's feed writes them. */
export function userManagedBy(ctx: UserRecordEditContext): string | null {
  const names = [...new Set(feedingBookings(ctx).map((b) => b.managed_by_display ?? b.managed_by ?? ''))].filter(Boolean);
  if (names.length === 0) return null;
  return names.length === 1 ? names[0]! : 'A booking system';
}

/** The ledger banner's sentence: which feeds, through how many open bookings — and the
 *  open bookings beyond the cap that were not checked. */
export function userManagedNote(ctx: UserRecordEditContext): string | undefined {
  const feeding = feedingBookings(ctx);
  if (feeding.length === 0) return undefined;
  const names = [...new Set(feeding.map((b) => b.managed_by_display ?? b.managed_by ?? ''))].filter(Boolean);
  const n = feeding.length;
  const through = `${names.join(' and ')} ${names.length === 1 ? 'writes' : 'write'} this person through ${n} open ${n === 1 ? 'booking' : 'bookings'}`;
  const more = ctx.restech.more;
  return more > 0 ? `${through}; ${more} more open ${more === 1 ? 'booking was' : 'bookings were'} not checked` : through;
}

export interface UserEditorConfigInput {
  catalog: UserRecordCatalog;
  editContext: UserRecordEditContext;
  /** The users detail — names of the user's current tags. */
  detail?: Pick<ConsoleUserDetail, 'tags' | 'first_name' | 'last_name' | 'email'> | null;
  /** A ledger row for a field not on screen (another page tab). */
  onFocusMissing?: (rec: RecordKey, path: string) => void;
}

/** Why Edit is not offered on this user, or null — the kill switch and a soft-deleted user
 *  (permissions aside: no permission, no Edit button at all). */
export function userEditBlockedReason(catalog: UserRecordCatalog, ctx: UserRecordEditContext): string | null {
  if (!catalog.enabled || !ctx.flags.user_edit_enabled) return EDITING_OFF;
  if (ctx.is_deleted) return 'Deleted users are read-only — restore the user to edit it';
  return null;
}

/** The pure half of the config (everything but the hooks and pickers), for tests. */
export function userEditorConfigParts({
  catalog,
  editContext: ctx,
  detail,
  onFocusMissing,
}: UserEditorConfigInput): Omit<RecordEditorConfig, 'usePreview' | 'useApply' | 'widgets'> {
  const userRec = recordKey('user', ctx.user_id);
  const managedBy = userManagedBy(ctx);
  const managedNote = userManagedNote(ctx);
  const fields = catalog.fields.map(recordField);

  const verdictOf = (rec: RecordKey, field: RecordField): RecordVerdict | null => {
    if (!managedBy || rec !== userRec) return null;
    return ctx.restech.verdicts[field.key] ?? null;
  };

  const editabilityOf = (rec: RecordKey, field: RecordField) => {
    const blocked = userEditBlockedReason(catalog, ctx);
    if (blocked) return { editable: false, reason: blocked };
    if (!ctx.permissions[USER_MANAGE_PERM]) return { editable: false, reason: `Needs the ${USER_MANAGE_PERM} permission` };
    const missing = field.permissions.find((p) => !ctx.permissions[p]);
    if (missing) return { editable: false, reason: `Needs the ${missing} permission` };
    const guards = ctx.blocked[`${rec}:${field.key}`] ?? [];
    if (guards.length > 0) {
      return { editable: false, reason: guards.map((g) => catalog.guards[g] ?? g).join(' ') };
    }
    if (field.widget === 'tags' && !ctx.tenant) {
      return { editable: false, reason: 'Tags belong to a tour operator, and this user has none' };
    }
    return { editable: true };
  };

  // The contacts questions: asked when the user email / phone row is removed or disabled
  // and the draft names no other (the server's rule), answered by an enabled row of the
  // draft — or, for the phone, by "No user phone".
  const storedContacts = asContactsValue(ctx.baselines[userRec]?.contacts);
  const draftContacts = (state: DraftState): ContactsValue | null => {
    const entry = state.entries[userRec]?.contacts;
    return entry ? asContactsValue(entry.to) : null;
  };
  const isOptionAsked = (option: RecordOption, state: DraftState): boolean | undefined => {
    const type = ADDRESS_OPTIONS[option.key];
    if (!type) return undefined;
    const to = draftContacts(state);
    return to ? userAddressAsked(storedContacts, to, type) : false;
  };
  const resolveOption = (option: RecordOption, state: DraftState): RecordOption => {
    const type = ADDRESS_OPTIONS[option.key];
    if (!type) return option;
    const to = draftContacts(state) ?? storedContacts;
    return { ...option, values: [...userAddressAnswers(to, type), ...option.values] };
  };

  const name = detail ? userDisplayName({ id: ctx.user_id, ...detail }) : `User #${ctx.user_id}`;
  const groupOf = () => ({ id: 'user', title: name });

  const optionRefs: Record<string, RecordOptionRef[]> = {
    tags: (detail?.tags ?? []).map((t) => ({
      id: t.id,
      label: t.name,
      group: t.category?.name ?? null,
      tenant: ctx.tenant?.code ?? null,
    })),
  };

  // One user.updated per save — only when Emit is chosen (default Suppress).
  const estimateEvents = (state: DraftState) =>
    Object.keys(state.entries[userRec] ?? {}).length > 0 ? [{ code: 'user.updated', guest_visible: false }] : [];

  return {
    fields,
    options: catalog.options.map(recordOption),
    actions: [],
    effects: catalog.effects,
    timezone: USER_TIMEZONE,
    recordNoun: 'person',
    managedBy,
    ...(managedNote ? { managedNote } : {}),
    verdictOf,
    editabilityOf,
    groupOf,
    recordOrder: [userRec],
    isOptionAsked,
    resolveOption,
    provenanceOf: (field) =>
      field.kind === 'vendor'
        ? 'Set by the app or the online travel agency'
        : [field.restech_note, field.help].filter(Boolean).join(' ') || undefined,
    optionRefs,
    estimateEvents,
    confirmFollowUps: [],
    eventStatusLabel: (s) => EVENT_STATUS_LABELS[s] ?? s.replace(/_/g, ' '),
    maxChanges: catalog.max_changes,
    descriptionMaxLength: catalog.description_max_length,
    restechHelpStory: 'users-detail-page',
    ...(onFocusMissing ? { onFocusMissing } : {}),
  };
}

/** The user's remote pickers: the account tags among its tenant's tags — with tenant + pk
 *  on every option. No tenant (a system user): no picker (the field says why). */
export function userWidgets(ctx: Pick<UserRecordEditContext, 'tenant'>): Record<string, RecordWidget> {
  const tenantId = ctx.tenant?.id;
  if (tenantId === undefined) return {};
  return {
    tags: createTagsWidget({
      load: async (_f, _r, q, signal) =>
        (await tagOptions(tenantId, q, undefined, signal)).map((t) => ({
          id: t.id,
          label: t.name,
          group: t.category.name,
          tenant: t.tenant?.code ?? null,
        })),
    }),
  };
}

/** The user editor's `RecordEditorConfig` — stable while its inputs are. */
export function useUserRecordEditorConfig(input: UserEditorConfigInput): RecordEditorConfig {
  const { catalog, editContext, detail, onFocusMissing } = input;
  const id = editContext.user_id;
  const tenantId = editContext.tenant?.id;
  const widgets = useMemo(
    () => userWidgets({ tenant: editContext.tenant }),
    // The pickers depend on the tenant only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tenantId],
  );
  return useMemo<RecordEditorConfig>(
    () => ({
      ...userEditorConfigParts({ catalog, editContext, detail, onFocusMissing }),
      widgets,
      usePreview: (body) => useUserChangesetPreview(id, contactsPreviewBody(body)),
      useApply: (opts) => useApplyUserChangeset(id, opts),
    }),
    [catalog, editContext, detail, onFocusMissing, widgets, id],
  );
}
